import React, { useEffect, useState } from "react";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../../components/ui/table";
import Button from "../../components/ui/Button";
import { Badge } from "../../components/ui/badge";
import { Card } from "../../components/ui/card";
import Modal from "../../components/ui/Modal";
import Toggle from "../../components/ui/Toggle";
import { useToast } from "../../contexts/ToastContext";
import { adminAPI } from "../../services/api/admin";

export default function AdminStripeEvents() {
  const { showToast } = useToast();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ event_type: "", client_id: "", unprocessed: false, with_errors: false });
  const [viewing, setViewing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.event_type) params.event_type = filters.event_type;
      if (filters.client_id) params.client_id = filters.client_id;
      if (filters.unprocessed) params.unprocessed = "true";
      if (filters.with_errors) params.with_errors = "true";
      params.limit = 100;
      setEvents(await adminAPI.getStripeEvents(params));
    } catch (e) { showToast("Failed to load events", "error"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filters]);

  const replay = async (id) => {
    try {
      await adminAPI.replayStripeEvent(id);
      showToast("Replayed", "success");
      load();
    } catch (e) { showToast(e.response?.data?.error || "Replay failed", "error"); }
  };

  const view = async (id) => {
    try { setViewing(await adminAPI.getStripeEvent(id)); }
    catch (e) { showToast("Failed to load event", "error"); }
  };

  const cols = [
    { title: "Received", dataIndex: "created_at", render: v => new Date(v).toLocaleString() },
    { title: "Event ID", dataIndex: "stripe_event_id", render: v => <code style={{ fontSize: 11 }}>{v}</code> },
    { title: "Type", dataIndex: "event_type" },
    { title: "Workspace",
      render: (_, r) => r.client_id
        ? <span>{r.client_name || `#${r.client_id}`} <span className="text-muted-foreground">· {r.client_id}</span></span>
        : <span className="text-muted-foreground">—</span> },
    { title: "Status",
      render: (_, r) => r.error ? <Badge variant="danger">error</Badge> :
                         r.processed_at ? <Badge variant="success">processed</Badge> :
                         <Badge variant="warning">pending</Badge> },
    { title: "",
      render: (_, r) => (
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={() => view(r.id)}>View</Button>
          <Button variant="secondary" onClick={() => replay(r.id)}>Replay</Button>
        </div>
      ) }
  ];

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold text-foreground">Stripe events</h1>
      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-2">
          <input placeholder="Filter by event type"
                 value={filters.event_type}
                 onChange={e => setFilters(f => ({ ...f, event_type: e.target.value }))}
                 className="rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                 style={{ width: 260 }} />
          <input placeholder="Filter by workspace (client_id)"
                 value={filters.client_id}
                 onChange={e => setFilters(f => ({ ...f, client_id: e.target.value }))}
                 className="rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                 style={{ width: 220 }} />
          <span className="text-sm text-foreground">Unprocessed only</span>
          <Toggle enabled={filters.unprocessed}
                  onToggle={() => setFilters(f => ({ ...f, unprocessed: !f.unprocessed }))} />
          <span className="text-sm text-foreground">Errors only</span>
          <Toggle enabled={filters.with_errors}
                  onToggle={() => setFilters(f => ({ ...f, with_errors: !f.with_errors }))} />
        </div>
      </Card>
      <div className="overflow-hidden rounded-xl border border-border">
        <Table>
          <TableHeader>
            <TableRow>
              {cols.map((c, i) => <TableHead key={i}>{c.title}</TableHead>)}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={cols.length} className="text-center text-muted-foreground">Loading…</TableCell>
              </TableRow>
            ) : events.length === 0 ? (
              <TableRow>
                <TableCell colSpan={cols.length} className="text-center text-muted-foreground">No data</TableCell>
              </TableRow>
            ) : events.map(r => (
              <TableRow key={r.id}>
                {cols.map((c, i) => (
                  <TableCell key={i}>
                    {c.render ? c.render(c.dataIndex ? r[c.dataIndex] : undefined, r) : r[c.dataIndex]}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {!!viewing && (
        <Modal onClose={() => setViewing(null)} maxWidth="max-w-3xl"
               title={viewing && `Event ${viewing.stripe_event_id}`}>
          {viewing && (
            <div className="space-y-2">
              <div><strong>Type:</strong> {viewing.event_type}</div>
              <div><strong>Processed:</strong> {viewing.processed_at ? new Date(viewing.processed_at).toLocaleString() : "—"}</div>
              {viewing.error && <div><strong>Error:</strong> <pre style={{ color: "red", whiteSpace: "pre-wrap" }}>{viewing.error}</pre></div>}
              <div><strong>Payload:</strong>
                <pre style={{ background: "#f6f6f6", padding: 12, fontSize: 11, maxHeight: 400, overflow: "auto" }}>
                  {JSON.stringify(viewing.payload, null, 2)}
                </pre>
              </div>
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}
